import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { formatDate } from "@/lib/orderUtils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Phone, MailOpen, Inbox } from "lucide-react";

export default function MessagesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contacts");
      setItems(data);
    } catch (e) {
      toast.error("Errore nel caricamento messaggi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}/read`);
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const unread = items.filter((m) => !m.read).length;

  return (
    <DashboardLayout title="Messaggi" subtitle="Richieste di contatto ricevute dal sito.">
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Inbox className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-4 font-medium text-slate-700">Nessun messaggio</p>
          <p className="text-sm text-slate-500">Le richieste inviate dal form contatti compariranno qui.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="messages-list">
          <p className="text-sm text-slate-500">{unread} non letti su {items.length}</p>
          {items.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <Card data-testid="message-card" className={`p-5 border-slate-200 ${m.read ? "" : "border-l-4 border-l-teal-500"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{m.name}</h3>
                      {!m.read && <Badge className="bg-teal-100 text-teal-700 border-0 hover:bg-teal-100">Nuovo</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{m.email}</span>
                      {m.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{m.phone}</span>}
                      <span className="text-xs text-slate-400">{formatDate(m.created_at)}</span>
                    </div>
                    <p className="mt-3 text-slate-700 whitespace-pre-wrap">{m.message}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <a data-testid="reply-gmail-button" href={`https://mail.google.com/mail/?view=cm&fs=1&authuser=officinadentale.3dprint@gmail.com&to=${encodeURIComponent(m.email)}&su=${encodeURIComponent(`Re: richiesta dal sito Officina Dentale 3D Print`)}&body=${encodeURIComponent(`Gentile ${m.name},\n\n\n\n---\nIl suo messaggio:\n${m.message}`)}`} target="_blank" rel="noreferrer"><Button size="sm" className="bg-teal-600 hover:bg-teal-700"><Mail className="h-4 w-4 mr-1.5" /> Rispondi con Gmail</Button></a>
                    {!m.read && (
                      <Button size="sm" variant="outline" data-testid="mark-read-button" onClick={() => markRead(m.id)}>
                        <MailOpen className="h-4 w-4 mr-1.5" /> Segna letto
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
